/**
 * Dependency Injection Container. Singleton instances only.
 */
class DiContainer {
  /**
   * Injectable instances metadata
   */
  protected entries: DiContainerEntry[] = [];

  private readonly circularDependencyControl = new Set<DiContainerEntry>();

  constructor() {
    window.di = this;
  }

  /**
   * Add injectable instance.
   * @param type injectable type (constructor function).
   * @param [typeAliases] optional list of string aliases by which this instance can be resolved.
   */
  public add<T extends object>(type: new (...args: any[]) => T, typeAliases?: string[]): void {
    // NOTE: Don't use `type.name` as an identifier, since after minification all type names will be minified too.

    if (this.entries.some((entry) => entry.type === type)) {
      throw new Error(`[Dependency Injection Container] injectable with such name already exists: ${type.name}`);
    }
    typeAliases = typeAliases || [];
    if (type.name.length > 2) {
      // If not minified
      typeAliases.unshift(type.name);
    }
    this.entries.push({
      type: type,
      aliases: typeAliases,
    });
  }

  /**
   * Get injectable instance by string type alias
   * @param {string} alias
   */
  public get<T extends object>(alias: string): T;

  /**
   * Get injectable instance by type (constructor function)
   * @param {Function} type
   */
  public get<T extends object>(type: new (...args: any[]) => T): T;

  /**
   * Get injectable instance by type or string type alias
   * @param {string|Function} typeOrAlias
   */
  public get<T extends object>(typeOrAlias: string | (new (...args: any[]) => T)): T;

  /**
   * Get injectable instance by type or string type alias
   * @param {string|Function} typeOrAlias
   */
  public get<T extends object>(typeOrAlias: string | (new (...args: any[]) => T)): T {
    const entry = this.entries.find((entry) => {
      if (typeof typeOrAlias === 'string') {
        return entry.aliases.includes(typeOrAlias);
      }
      return entry.type === typeOrAlias;
    });

    if (!entry) {
      const str = typeof typeOrAlias === 'string' ? `"${typeOrAlias}"` : typeOrAlias?.name || typeOrAlias?.toString();
      throw new Error(`[Dependency Injection Container] There is no ${str} in Dependency Injection Container`);
    }

    if (!entry.instance && entry.type) {
      this.instantiateInjectable(entry);
    }

    return entry.instance! as T;
  }

  /**
   * Get injectable instances by string type alias.
   * @param alias string type alias
   */
  public getMany<T extends object[]>(alias: string): T {
    return this.entries
      .filter((entry) => entry.aliases.includes(alias))
      .map((e) => {
        if (!e.instance && e.type) {
          this.instantiateInjectable(e);
        }
        return e.instance;
      })
      .filter((e) => e != null) as T;
  }

  private instantiateInjectable(entry: DiContainerEntry) {
    if (!entry.type) {
      return;
    }

    if (this.circularDependencyControl.has(entry)) {
      const trail = Array.from(this.circularDependencyControl)
        .map((e) => `[${this.getEntryName(e)}]`)
        .join(' -> ');
      this.circularDependencyControl.clear();

      // Achievement received: creating Ouroboros 🐍↺
      throw new Error(`[Dependency Injection Container] Circular dependency: ${trail} -> [Circular Dependency]`);
    }

    this.circularDependencyControl.add(entry);

    try {
      entry.instance = new entry.type();
    } catch (err) {
      this.circularDependencyControl.delete(entry);
      if (Error && err instanceof Error && !err.message?.startsWith('[Dependency Injection Container]')) {
        console.error(`[Dependency Injection Container] Unable to instantiate injectable: ${this.getEntryName(entry)}`);
      }

      // NOTE: If we don't want to put half of the application down, but rather crash quietly, leaving only a trace in
      // the console and undefined instead of a dependency, then we just need to remove rethrow from here.
      throw err;
    }

    this.circularDependencyControl.delete(entry);
  }

  /**
   * Get human-readable representation of injectable
   */
  private getEntryName(entry: DiContainerEntry): string {
    return entry.type?.name || entry.aliases?.join('|') || entry.instance?.constructor?.name || 'Unknown injectable';
  }

  /**
   * Clear Dependency Injection Container.
   * Use this feature for testing purposes only.
   */
  public clear(): void;

  /**
   * Remove from the Dependency Injection container instances and declarations by type (constructor function).
   * Use this feature for testing purposes only.
   * @param type
   */
  public clear(type: new (...args: any[]) => any): void;

  /**
   * Remove from the Dependency Injection container instances and declarations by string type alias.
   * Use this feature for testing purposes only.
   * @param alias
   */
  public clear(alias: string): void;

  /**
   * Clear Dependency Injection Container. You can specify a specific type or a string type alias of a type to remove
   * only those. Use this feature for testing purposes only.
   * @param {string|Function} typeOrAlias type (constructor function) or string type alias
   */
  public clear(typeOrAlias?: string | (new (...args: any[]) => any)): void {
    this.circularDependencyControl.clear();

    if (typeOrAlias) {
      this.entries = this.entries.filter((entry) => {
        if (typeof typeOrAlias === 'string') {
          return !entry.aliases.includes(typeOrAlias);
        }
        return entry.type !== typeOrAlias;
      });
    } else {
      this.entries = [];
    }
  }

  /**
   * Provide instance manually for type or string type alias.
   * This function should mainly be used in tests to replace dependencies with mocks.
   */
  public provide<T extends object>(typeOrAlias: string | (new (...args: any[]) => T), instance: T): void {
    const entry = this.entries.find((entry) => entry.type === typeOrAlias);
    if (entry) {
      entry.instance = instance;
    } else if (typeof typeOrAlias === 'string') {
      this.entries.push({
        aliases: [typeOrAlias],
        instance: instance,
      });
    } else {
      this.entries.push({
        type: typeOrAlias,
        aliases: [],
        instance: instance,
      });
    }
  }
}

/**
 * Dependency Injection Container. Singleton instances only.
 */
export const diContainer = new DiContainer();

interface DiContainerEntry<T = object> {
  type?: new (...args: any[]) => T;
  aliases: string[];
  instance?: T;
}

declare global {
  interface Window {
    di: DiContainer;
  }

  const di: DiContainer;
}
